import { Box, Typography } from '@mui/material';

import { Card, Section } from './Card';
import { BAND_LABEL_MIN_WEEKS, COLORS, GAP_COLORS, GAP_LABELS } from './constants';
import { describeWeek, fillWeekGaps, isGapStatus, normalizeWeeks } from './weeks';

const CHART_HEIGHT = 72;

// Away is a solid tint and Break is striped, so the two differ by more than colour.
const BREAK_STRIPES = (color) =>
  `repeating-linear-gradient(45deg, ${color}, ${color} 3px, transparent 3px, transparent 6px)`;
const bandSx = (kind) => {
  if (kind === 'away') {
    return { backgroundColor: GAP_COLORS.away };
  }
  if (kind === 'break') {
    return { backgroundColor: GAP_COLORS.break, backgroundImage: BREAK_STRIPES(GAP_COLORS.break) };
  }
  if (kind === 'missing') {
    return { border: `1px dashed ${COLORS.border}` };
  }
  return {};
};

const kindOf = (w) => (isGapStatus(w.status) || w.status === 'missing' ? w.status : 'none');

// Consecutive weeks of the same kind become one segment under the bars (gap kinds are labelled).
export const buildWeekSegments = (weeks) => {
  const segments = [];
  fillWeekGaps(weeks).forEach((w) => {
    const kind = kindOf(w);
    const last = segments[segments.length - 1];
    if (last && last.kind === kind) {
      last.count += 1;
    } else {
      segments.push({ kind, count: 1, key: w.weekStart });
    }
  });
  return segments;
};

// Short bands get a one letter fallback: the full word would be clipped to nothing.
export const segmentText = (segment) => {
  if (!isGapStatus(segment.kind)) {
    return '';
  }
  const label = GAP_LABELS[segment.kind];
  return segment.count >= BAND_LABEL_MIN_WEEKS ? label : label.charAt(0);
};

export const summarizeWeeks = (weeks) => {
  const list = normalizeWeeks(weeks);
  const count = (status) => list.filter((w) => w.status === status).length;
  return {
    total: list.length,
    active: count('active'),
    away: count('away'),
    breaks: count('break'),
    idle: count('idle'),
    missing: fillWeekGaps(weeks).length - list.length,
  };
};

const LegendItem = ({ swatch, children }) => (
  <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      aria-hidden="true"
      sx={{
        width: 16,
        height: 16,
        borderRadius: 0.5,
        border: `1px solid ${COLORS.border}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        ...swatch.sx,
      }}
    >
      {swatch.text}
    </Box>
    <span>{children}</span>
  </Box>
);

const Legend = ({ showGaps }) => (
  <Box
    component="ul"
    aria-label="Weekly activity key"
    data-testid="weekly-activity-legend"
    sx={{ p: 0, m: 0, mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: 13 }}
  >
    <LegendItem swatch={{ sx: { backgroundColor: COLORS.accent } }}>Sessions</LegendItem>
    {showGaps && (
      <>
        <LegendItem swatch={{ text: 'A', sx: bandSx('away') }}>Away (A)</LegendItem>
        <LegendItem swatch={{ text: 'B', sx: bandSx('break') }}>Break (B)</LegendItem>
      </>
    )}
    <LegendItem swatch={{ sx: {} }}>No sessions</LegendItem>
  </Box>
);

const WeeklyActivity = ({ weeks }) => {
  const list = fillWeekGaps(weeks);
  if (list.length === 0) {
    return null;
  }
  const max = Math.max(1, ...list.map((w) => w.sessions));
  const summary = summarizeWeeks(weeks);
  const segments = buildWeekSegments(weeks);
  const summaryText = `Weekly sessions over ${list.length} weeks: ${summary.active} active, ${
    summary.idle
  } idle${summary.away ? `, ${summary.away} away` : ''}${
    summary.breaks ? `, ${summary.breaks} break` : ''
  }${summary.missing ? `, ${summary.missing} with no data` : ''}.`;

  return (
    <Section title="Weekly activity" id="perf-weekly">
      <Card>
        <Box role="img" aria-label={summaryText} data-testid="weekly-activity">
          <Box aria-hidden="true">
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: CHART_HEIGHT }}>
              {list.map((w) => (
                <Box
                  key={w.weekStart}
                  title={describeWeek(w)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    borderBottom: `1px solid ${COLORS.border}`,
                    ...bandSx(kindOf(w)),
                  }}
                >
                  {w.sessions > 0 && (
                    <Box
                      data-testid="week-bar"
                      sx={{
                        width: '100%',
                        height: `${Math.max(12, (w.sessions / max) * 100)}%`,
                        backgroundColor: COLORS.accent,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: '2px', mt: 0.5, minHeight: 20 }}>
              {segments.map((s) => (
                <Box
                  key={s.key}
                  sx={{
                    flex: s.count,
                    minWidth: 0,
                    fontSize: 11,
                    fontWeight: 700,
                    textAlign: 'center',
                    // Letters on narrow bands may spill a little rather than clip to nothing.
                    overflow: s.count >= BAND_LABEL_MIN_WEEKS ? 'hidden' : 'visible',
                    whiteSpace: 'nowrap',
                    borderRadius: 0.5,
                    ...bandSx(s.kind),
                  }}
                >
                  {segmentText(s)}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Legend showGaps={summary.away + summary.breaks > 0} />
        <Box
          component="details"
          data-testid="weekly-activity-details"
          sx={{ mt: 1.5, fontSize: 13 }}
        >
          <summary>Show week by week details</summary>
          <Box component="ul" sx={{ mt: 1, pl: 2 }} data-testid="weekly-activity-list">
            {list.map((w) => (
              <li key={w.weekStart}>{describeWeek(w)}</li>
            ))}
          </Box>
        </Box>
        <Typography sx={{ mt: 1.5, fontSize: 13, opacity: 0.8 }}>
          Bars show sessions per week. Shaded weeks labelled Away or Break are time off, not missing
          data. Empty weeks had no sessions.
        </Typography>
      </Card>
    </Section>
  );
};

export default WeeklyActivity;
